import type { Router } from "vue-router";
import authService from "@/core/services/AuthenticationService";

// Every page is its own chunk, named by a hash of its build. A tab opened before a release
// still asks for the old names, which the release deleted, so the import fails -- nginx
// answers with a 404, or with index.html on an older config. Reloading picks up the new
// release's index.html and with it the new names.

// How each browser reports a lazy module it could not load: Chrome and Edge, Firefox,
// Safari. The last is Vite's own, when a page's stylesheet is the part that is missing.
const STALE_MODULE = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

const RELOADED_AT = "new-release-reloaded-at";

// Failing again this soon after reloading means the file is missing from the new release
// too. Reloading again would not help, and would loop.
const RETRY_WINDOW_MS = 10_000;

// Indirect so the unit tests can stand in for it; jsdom implements neither.
export const browser = {
    assign: (url: string) => window.location.assign(url),
    reload: () => window.location.reload(),
};

export function isStaleModuleError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return STALE_MODULE.test(message);
}

/**
 * Reload, to `url` if given, unless this tab already did within RETRY_WINDOW_MS.
 * Resolves to whether it reloaded.
 */
export async function reloadForNewRelease(url?: string): Promise<boolean> {
    try {
        const reloadedAt = Number(sessionStorage.getItem(RELOADED_AT)) || 0;
        if (Date.now() - reloadedAt < RETRY_WINDOW_MS) return false;
        sessionStorage.setItem(RELOADED_AT, String(Date.now()));
    } catch {
        // Without storage nothing would stop a loop, so leave the error standing.
        return false;
    }

    // A token refresh cut off by the reload would leave the new page logged out. The claim
    // above is already stored, so nothing else reloads while this waits.
    await authService.whenRefreshSettled();

    if (url) browser.assign(url);
    else browser.reload();
    return true;
}

export function reloadOnNewRelease(router: Router) {
    // A page's chunk fails while navigating to it, so reload straight into that page.
    router.onError((error, to) => {
        if (isStaleModuleError(error)) reloadForNewRelease(router.resolve(to).href);
    });

    // Vite reports the same failure here first. Deferred so that, mid-navigation, the
    // router's handler above runs first and keeps the destination; this only reloads for
    // a lazy import outside navigation, which the router never sees.
    window.addEventListener("vite:preloadError", () => {
        setTimeout(() => reloadForNewRelease(), 0);
    });
}
